/**
 * Properties that must travel with `canvas.toJSON()`.
 *
 * When you add a serialized field, declare it in `shared/types/fabric.d.ts` and
 * add it to this list with a one-line comment explaining what it's for.
 */
export const FABRIC_SERIALIZED_PROPERTIES = {
  propertiesToInclude: [
    // Identity / cross-resolution linking.
    'layerId',
    'pieceId',
    'linkedAcrossResolutions',

    // Timeline track-row identity — absent means "shared video/text group row" (see timeline/utils/timeline-rows.ts).
    'laneKey',

    // Video<->subtitle link (temporal) — absent means "not attached to a video".
    'timeAttach',

    // Produced by an external pipeline, not hand-placed by the user — see model/types.ts's Originable.
    'sceneDerived',

    // Layout-driven behaviors (see utils/).
    'autoFit',
    'imageFit',
    'imageBox',
    'wrapText',
    'fitToContent',
    'attachTo',
    'highlightRanges',
    'textStyleRules',

    // Pill component metadata (see pill/).
    'pill',

    // Sobreimpreso component metadata (see sobreimpreso/).
    'sobreimpreso',

    // Group collection metadata (see grouping/).
    'groupCollection',

    // Interaction flags (Fabric omits these by default).
    'selectable',
    'evented',
    'lockMovementX',
    'lockMovementY',
    'lockScalingX',
    'lockScalingY',
    'lockUniScaling',
    'lockRotation',
    'deletable', // Blocks delete handlers when false.
    'layerVisible', // User's eye-icon choice (distinct from transient `visible`).

    // Per-object animation config (legacy path).
    'assignedAnimation',
    'animationDelay',
    'animationDuration',

    // Timeline clip lifetime — user-editable start/duration that override
    // canvas-derived defaults (animation delay / video native duration / fill).
    'clipStart',
    'clipDuration',

    // Required so `objectCaching: false` (set by useGrouping to avoid bilinear
    // softening of Fabric's cache bitmaps) survives loadFromJSON during export.
    'objectCaching',

    // Effect: alpha falloff — see hooks/use-alpha-falloff.ts.
    'alphaFalloffEnabled',
    'alphaFalloffParams',

    // Effect: chroma key — see hooks/use-chroma-key.ts.
    'chromaKeyEnabled',
    'chromaKeyParams',

    // URL-referenced media; hydrator rebuilds HTMLVideoElement instances from these.
    'videoSrc',
    'audioEnabled'
  ] as const
};
